import { AxiosError } from "axios";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import * as React from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import { useMount } from "react-use";
import { Toaster } from "sonner";
import DrawerForm from "src/components/drawer-form/drawer-form";
import SupportForm from "src/components/support-form/support-form";
import { TwoFactorModal } from "src/components/two-factor/two-factor-modal";
import useSharedMerchant from "src/hooks/use-merchant";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import useSharedMerchants from "src/hooks/use-merchants";
import useSharedPosthogStatus from "src/hooks/use-posthog-status";
import { useSensitiveOperation } from "src/hooks/use-sensitive-operation";
import { AppLayout, type MenuItem } from "src/layouts/app-layout";
import { showToast } from "src/lib/toast-helper";
import AcceptInvitationPage from "src/pages/accept-invitation-page/accept-invitation-page";
import AccountSettingsPage from "src/pages/account-settings-page/account-settings-page";
import CustomerDetailView from "src/pages/customers-page/customer-detail-view";
import CustomersPage from "src/pages/customers-page/customers-page";
import DashboardPage from "src/pages/dashboard-page/dashboard-page";
import DeveloperPage from "src/pages/developer-page/developer-page";
import {
	AddressesList,
	BalancesList,
	FinancesLayout,
	WithdrawalsList,
} from "src/pages/finances-page";
import ForgotPasswordPage from "src/pages/forgot-password-page/forgot-password-page";
import LoginPage from "src/pages/login-page/login-page";
import ManageMerchantsPage from "src/pages/manage-merchants-page/manage-merchants-page";
import NotificationsPage from "src/pages/notifications-page/notifications-page";
import OrdersPage from "src/pages/orders-page/orders-page";
import OTPVerificationPage from "src/pages/otp-verification-page/otp-verification-page";
import PaymentLinksPage from "src/pages/payment-links-page/payments-links-page";
import {
	PaymentDetail,
	PaymentSettings,
	PaymentsLayout,
	PaymentsList,
} from "src/pages/payments-page";
import ResetPasswordPage from "src/pages/reset-password-page/reset-password-page";
import Setup2FAPage from "src/pages/setup-2fa-page/setup-2fa-page";
import authProvider from "src/providers/auth-provider";
import merchantProvider from "src/providers/merchant-provider";
import type { SupportMessage, User } from "src/types";
import { sleep } from "src/utils";
import { ENTITY_CONFIG } from "src/utils/entity-config";
import { toggled } from "./providers/toggles";

const defaultMenus: MenuItem[] = [
	{
		path: "/dashboard",
		name: "Dashboard",
		icon: ENTITY_CONFIG.dashboard.icon,
		color: ENTITY_CONFIG.dashboard.color,
	},
	{
		path: "/orders",
		name: "Orders",
		icon: ENTITY_CONFIG.orders.icon,
		color: ENTITY_CONFIG.orders.color,
	},
	{
		path: "/payments",
		name: "Payments",
		icon: ENTITY_CONFIG.payments.icon,
		color: ENTITY_CONFIG.payments.color,
	},
	{
		path: "/payment-links",
		name: "Payment Links",
		icon: ENTITY_CONFIG.payment_links.icon,
		color: ENTITY_CONFIG.payment_links.color,
	},
	{
		path: "/finances",
		name: "Finances",
		icon: ENTITY_CONFIG.finances.icon,
		color: ENTITY_CONFIG.finances.color,
	},
];

// Configuration surfaces — visually demoted to the bottom of the sidebar
// (above the merchant selector) so the daily operational nav stays at the
// top. Stripe/Mercury convention.
const defaultBottomMenus: MenuItem[] = [
	{
		path: "/developer",
		name: "Developer",
		icon: ENTITY_CONFIG.developer.icon,
		color: ENTITY_CONFIG.developer.color,
	},
	{
		path: "/notifications",
		name: "Notifications",
		icon: ENTITY_CONFIG.notifications.icon,
		color: ENTITY_CONFIG.notifications.color,
	},
	{
		path: "/manage-merchants",
		name: "Manage Merchants",
		icon: ENTITY_CONFIG.manage_merchants.icon,
		color: ENTITY_CONFIG.manage_merchants.color,
	},
];

// Preserves :customerId when bouncing legacy /customers/:customerId bookmarks
// to the new /payments/customers/:customerId location.
const LegacyCustomerRedirect: React.FC = () => {
	const { customerId } = useParams<{ customerId: string }>();
	return (
		<Navigate
			to={`/payments/customers${customerId ? `/${customerId}` : ""}`}
			replace
		/>
	);
};

if (toggled("feedback")) {
	defaultMenus.push({
		path: "/support",
		name: "Support / Feedback",
		icon: ENTITY_CONFIG.support.icon,
		color: ENTITY_CONFIG.support.color,
	});
}

interface AppLoadState {
	reloadUserInfo: boolean;
}

const App: React.FC = () => {
	const state: AppLoadState = useLocation().state;
	const posthog = usePostHog();
	const location = useLocation();
	const navigate = useNavigate();
	const { theme } = useTheme();

	const { merchantId, setMerchantId } = useActiveMerchantId();
	const { merchants, getMerchants } = useSharedMerchants();
	const { getMerchant } = useSharedMerchant();
	const { isPosthogActive } = useSharedPosthogStatus();
	const [user, setUser] = React.useState<User>();
	const [isSupportFormOpen, setIsSupportFormOpen] =
		React.useState<boolean>(false);
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const {
		isOpen: is2FAModalOpen,
		modalDetails,
		handleVerify: handle2FAVerify,
		handleCancel: handle2FACancel,
	} = useSensitiveOperation();

	const loadUserInfo = async () => {
		let newMerchantId = merchantId;
		let user: User;

		const getCookie = async () => {
			try {
				await authProvider.getCookie();
			} catch (e) {
				if (e instanceof AxiosError && e.response?.status === 401) {
					navigate("/login", {
						state: {
							isNeedLogout: true,
						},
					});
				}
			}
		};

		const getMe = async () => {
			try {
				user = await authProvider.getMe();
				setUser(user);
			} catch (e) {
				if (e instanceof AxiosError && e.response?.status === 401) {
					navigate("/login", {
						state: {
							isNeedLogout: true,
						},
					});
				}
			}
		};

		const listMerchants = async () => {
			if (!user) {
				return;
			}

			const merchants = await getMerchants();

			if (!merchantId) {
				newMerchantId = merchants[0]?.id;
				setMerchantId(newMerchantId);
			}
			return merchants;
		};

		const listMerchant = async () => {
			if (user && newMerchantId) {
				await getMerchant(newMerchantId);
			}
		};

		await getCookie();
		await getMe();
		await listMerchants();
		await listMerchant();
		setIsLoading(false);
	};

	useMount(async () => {
		const publicRoutes = [
			"/verify-otp",
			"/setup-2fa",
			"/accept-invitation",
			"/forgot-password",
			"/reset-password",
			"/login",
		];

		if (!publicRoutes.some((route) => location.pathname.startsWith(route))) {
			await loadUserInfo();
		}
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: loadUserInfo is stable
	React.useEffect(() => {
		if (state?.reloadUserInfo) {
			loadUserInfo();
		}
	}, [state]);

	React.useEffect(() => {
		if (user && isPosthogActive) {
			posthog?.identify(user.email, {
				email: user.email,
				uuid: user.uuid,
			});
		}
	}, [posthog, user, isPosthogActive]);

	const isManageMerchantsActive = location.pathname === "/manage-merchants";

	React.useEffect(() => {
		if (
			!isManageMerchantsActive &&
			merchants &&
			!merchants?.length &&
			location.pathname !== "/login"
		) {
			navigate("/manage-merchants");
			return;
		}

		if (location.pathname === "/") {
			navigate("/dashboard");
			return;
		}

		if (location.pathname === "/login") {
			document.title = "Login | Dashboard | Monbits Pay";
			return;
		}

		if (location.pathname === "/verify-otp") {
			document.title = "Two-Factor Authentication | Dashboard | Monbits Pay";
			return;
		}

		const pageName = [...defaultMenus, ...defaultBottomMenus].find(
			(item) => item.path === location.pathname,
		)?.name;
		document.title = `${pageName} | Dashboard | Monbits Pay`;
	}, [location, merchants, isManageMerchantsActive, navigate]);

	const logout = async () => {
		if (isPosthogActive) {
			posthog?.reset(true);
		}

		await authProvider.logout();
		navigate("/login", {
			state: {
				isNeedLogout: true,
			},
		});
	};

	const sendSupportForm = async (value: SupportMessage, reset: () => void) => {
		if (!merchantId) {
			console.error("No merchant ID available");
			return;
		}

		try {
			setIsFormSubmitting(true);
			await merchantProvider.sendSupportMessage(merchantId, value);
			setIsSupportFormOpen(false);
			showToast.info({
				message: "You have submitted a form",
				description: `Thank you for your ${value.topic.toLowerCase()}`,
				placement: "bottomRight",
			});

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const handleMerchantChange = async (newMerchantId: string) => {
		setMerchantId(newMerchantId);
		await getMerchant(newMerchantId);
	};

	// Add support form handler to menus
	const menusWithActions = defaultMenus.map((menu) => {
		if (menu.path === "/support") {
			return {
				...menu,
				onClick: () => setIsSupportFormOpen(true),
			};
		}
		return menu;
	});

	return (
		<>
			<Toaster
				position="bottom-right"
				richColors
				theme={theme as "light" | "dark" | "system"}
			/>
			<Routes>
				<Route path="login" element={<LoginPage />} />
				<Route path="verify-otp" element={<OTPVerificationPage />} />
				<Route path="setup-2fa" element={<Setup2FAPage />} />
				<Route path="accept-invitation" element={<AcceptInvitationPage />} />
				<Route path="forgot-password" element={<ForgotPasswordPage />} />
				<Route path="reset-password" element={<ResetPasswordPage />} />
				<Route
					path="*"
					element={
						<AppLayout
							menus={menusWithActions}
							bottomMenus={defaultBottomMenus}
							user={user}
							merchantId={merchantId || undefined}
							merchants={merchants}
							onMerchantChange={handleMerchantChange}
							onLogout={logout}
							showMerchantSelector={!isManageMerchantsActive}
							loading={isLoading}
						>
							<Routes>
								<Route path="dashboard" element={<DashboardPage />} />
								<Route
									path="account-settings"
									element={<AccountSettingsPage />}
								/>
								<Route path="payments" element={<PaymentsLayout />}>
									<Route index element={<PaymentsList />} />
									<Route path="settings" element={<PaymentSettings />} />
									<Route path="customers" element={<CustomersPage />}>
										<Route
											path=":customerId"
											element={<CustomerDetailView />}
										/>
									</Route>
									<Route path=":paymentId" element={<PaymentDetail />} />
								</Route>
								<Route path="orders" element={<OrdersPage />} />
								<Route path="payment-links" element={<PaymentLinksPage />} />
								<Route
									path="manage-merchants"
									element={<ManageMerchantsPage />}
								/>
								<Route path="finances" element={<FinancesLayout />}>
									<Route index element={<BalancesList />} />
									<Route path="withdrawals" element={<WithdrawalsList />} />
									<Route path="addresses" element={<AddressesList />} />
								</Route>
								<Route path="developer" element={<DeveloperPage />} />
								<Route path="notifications" element={<NotificationsPage />} />
								<Route path="customers" element={<LegacyCustomerRedirect />} />
								<Route
									path="customers/:customerId"
									element={<LegacyCustomerRedirect />}
								/>
								<Route path="*" element={<div>Not found</div>} />
							</Routes>

							<DrawerForm
								title="Contact us"
								description="Send us a message and we'll get back to you as soon as possible."
								isOpen={isSupportFormOpen}
								onClose={() => setIsSupportFormOpen(false)}
								onSubmit={() => {
									const form = document.querySelector("form");
									if (form) {
										form.dispatchEvent(
											new Event("submit", { cancelable: true, bubbles: true }),
										);
									}
								}}
								isSubmitting={isFormSubmitting}
								submitText="Send Message"
								width={512}
							>
								<SupportForm
									onFinish={sendSupportForm}
									isFormSubmitting={isFormSubmitting}
								/>
							</DrawerForm>
						</AppLayout>
					}
				/>
			</Routes>

			<TwoFactorModal
				open={is2FAModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						handle2FACancel();
					}
				}}
				onVerify={handle2FAVerify}
				title={modalDetails.title}
				description={modalDetails.description}
				showBackupOption={true}
			/>
		</>
	);
};

export default App;
